import React, { useState, useEffect } from 'react';
import { Idol } from 'types';
import { groupIdolsByType } from 'utils';
import IdolItem from './IdolItem';
import Tooltip from 'components/UI/Tooltip';
import EditIdolForm from './IdolForm/EditIdolForm';

interface IdolStashProps {
  stashItems: Idol[];
  onDragStart: (item: Idol) => void;
  onRemoveItem: (itemId: string) => void;
  onAddIdol: () => void;
  onEditIdol: (updatedIdol: Idol) => void;
  onDuplicateIdol: (idol: Idol) => void; // New prop for handling duplication
}

const IdolStash: React.FC<IdolStashProps> = ({ 
  stashItems, 
  onDragStart, 
  onRemoveItem, 
  onAddIdol, 
  onEditIdol,
  onDuplicateIdol
}) => {
  // Group idols for display - with safety check
  const groupedIdols = stashItems && Array.isArray(stashItems) ? groupIdolsByType(stashItems) : {};
  
  // Add tooltip state
  const [tooltipItem, setTooltipItem] = useState<Idol | null>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  
  // Edit modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [editingIdol, setEditingIdol] = useState<Idol | null>(null);

  // Effect to clear tooltip when stashItems changes (including when empty)
  useEffect(() => {
    // Clear tooltip when the last item is removed
    if (stashItems.length === 0) {
      setTooltipItem(null);
    }
    // Also check if current tooltip item still exists in stashItems
    else if (tooltipItem && !stashItems.some(item => item.id === tooltipItem.id)) {
      setTooltipItem(null);
    }
  }, [stashItems, tooltipItem]);

  // Handlers for tooltip
  const handleMouseEnter = (item: Idol, e: React.MouseEvent) => {
    setTooltipItem(item);
    setMousePosition({ x: e.clientX, y: e.clientY });
  };

  const handleMouseLeave = () => {
    setTooltipItem(null);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (tooltipItem) {
      setMousePosition({ x: e.clientX, y: e.clientY });
    }
  };
  
  // Edit handlers
  const handleEditClick = (idol: Idol) => {
    setEditingIdol(idol);
    setIsEditModalOpen(true);
  };
  
  const handleEditClose = () => {
    setIsEditModalOpen(false);
    setEditingIdol(null);
  };
  
  const handleSaveEditedIdol = (updatedIdol: Idol) => {
    onEditIdol(updatedIdol);
    setIsEditModalOpen(false);
    setEditingIdol(null);
  };

  // Duplicate handler
  const handleDuplicateIdol = (idol: Idol) => {
    onDuplicateIdol(idol);
  };

  return (
    <div 
      className="bg-gray-800 p-4 rounded-lg border border-gray-700 mb-4"
      onMouseMove={handleMouseMove}
    >
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-lg font-semibold text-white">Idols ({stashItems ? stashItems.length : 0})</h2>
          <div className="text-xs text-gray-400">Tip: Ctrl+V to paste idol from clipboard</div>
        </div>
        <button 
          className="bg-blue-600 hover:bg-blue-500 px-4 py-2 rounded transition-colors text-white font-medium"
          onClick={onAddIdol}
        >
          Add Idol
        </button>
      </div>
      
      {/* Scrollable container for idol groups */}
      <div className="max-h-[700px] overflow-y-auto pr-2 custom-scrollbar">
        {Object.keys(groupedIdols).length > 0 ? (
          Object.entries(groupedIdols).map(([typeName, items]) => (
            <div key={typeName} className="mb-4">
              <h3 className="text-base font-medium text-white mb-2">{typeName} ({items.length})</h3>
              <div className="space-y-2 mt-2">
                {items.map(item => (
                  <IdolItem
                    key={item.id}
                    item={item}
                    onDragStart={onDragStart}
                    onRemove={onRemoveItem}
                    onDuplicate={handleDuplicateIdol}
                    onEdit={handleEditClick}
                    onMouseEnter={(e) => handleMouseEnter(item, e)}
                    onMouseLeave={handleMouseLeave}
                  />
                ))}
              </div>
            </div>
          ))
        ) : (
          <p className="text-gray-500">No idols in stash. Add idols using the &quot;Add Idol&quot; button.</p>
        )}
      </div>

      {/* Render tooltip only when tooltipItem exists and stashItems is not empty */}
      {tooltipItem && stashItems.length > 0 && <Tooltip item={tooltipItem} position={mousePosition} />}
      
      {/* Edit Idol Modal */}
      <EditIdolForm
        isOpen={isEditModalOpen}
        onClose={handleEditClose}
        onSaveIdol={handleSaveEditedIdol}
        idol={editingIdol}
      />
    </div>
  );
};

export default IdolStash;